import { useEffect, useState } from 'react'
import { chatAvailabilityService, isChatHeartbeatFresh } from '../services/chatAvailabilityService'
import { logger } from '../utils/logger'

interface OnlineIndicatorProps {
  profileId: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export function OnlineIndicator({ profileId, showLabel = true, size = 'md' }: OnlineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(false)
  const [isAvailableDb, setIsAvailableDb] = useState(false)
  const [lastHeartbeat, setLastHeartbeat] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Carregar status inicial
    let mounted = true
    const loadStatus = async () => {
      try {
        const availability = await chatAvailabilityService.getAvailability(profileId)
        if (!mounted) return
        const available = availability?.is_available || false
        const hb = availability?.last_heartbeat || null
        setIsAvailableDb(available)
        setLastHeartbeat(hb)
        setIsOnline(available && isChatHeartbeatFresh(hb))
      } catch (err) {
        logger.error('Error loading online status', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadStatus()

    // Subscribe para mudanças em tempo real
    const subscription = chatAvailabilityService.subscribeToAvailability(
      profileId,
      (data) => {
        setIsAvailableDb(data.is_available)
        setLastHeartbeat(data.last_heartbeat)
        setIsOnline(data.is_available && isChatHeartbeatFresh(data.last_heartbeat))
      }
    )

    // Fallback: revalidar periodicamente caso realtime não esteja entregando eventos
    const pollInterval = setInterval(loadStatus, 2000)

    return () => {
      mounted = false
      if (subscription) {
        subscription.unsubscribe()
      }
      clearInterval(pollInterval)
    }
  }, [profileId])

  // Sem eventos no banco, o status poderia ficar "preso" em online; reavalia por TTL.
  useEffect(() => {
    if (!isAvailableDb) return

    const interval = setInterval(() => {
      setIsOnline(isAvailableDb && isChatHeartbeatFresh(lastHeartbeat))
    }, 10000)

    return () => {
      clearInterval(interval)
    }
  }, [isAvailableDb, lastHeartbeat])

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  }

  const labelSize = {
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm'
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-1">
        <div className={`${sizeClasses[size]} rounded-full bg-gray-400 animate-pulse`} />
        {showLabel && <span className={`${labelSize[size]} text-gray-400`}>Carregando...</span>}
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-1">
      <div
        className={`${sizeClasses[size]} rounded-full border border-white ${
          isOnline ? 'bg-green-500' : 'bg-gray-400'
        }`}
      />
      {showLabel && (
        <span className={`${labelSize[size]} ${isOnline ? 'text-green-400' : 'text-gray-400'}`}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      )}
    </div>
  )
}
